import { GateRequirement, SpeedyFlowUser } from '../types';

/**
 * Entitlement: whether the visitor may pass a gate, and how long to keep asking after a payment.
 *
 * These sit together because they are the same question asked at two moments. A purchase is
 * confirmed when the gate that opened the flow would now let the visitor through — never by a
 * Stripe redirect or a closed tab, both of which race the webhook that does the granting
 * (ADR 0003).
 */

/** Whether a `<key> -> ISO date` map grants `key` at `now`. */
export const isActive = (map: Record<string, string> | undefined, key: string, now: number) => {
  const until = map?.[key];
  return !!until && Date.parse(until) > now;
};

/**
 * Whether the visitor already satisfies the gate.
 *
 * Mirrors `createSelectCanAccessHabitat` and `selectHasExplorerFeature`, including the special-role
 * bypass: admins, partners, hosts and moderators carry the explorer feature without owning a
 * product, and must never be shown a purchase modal for something they can already reach.
 */
export const satisfiesGate = (gate: GateRequirement, user: SpeedyFlowUser, now: number): boolean => {
  if (!user.isLoggedIn) {
    return false;
  }
  if (gate.kind === 'account') {
    return true;
  }
  // Deliberately above the special-role shortcut: a confirmed address is a fact about the address,
  // and no role stands in for having confirmed one.
  if (gate.kind === 'verified-email') {
    return user.isRealEmail;
  }
  if (user.isSpecialRole) {
    return true;
  }
  if (gate.kind === 'feature') {
    return !!gate.feature && isActive(user.featuresUntil, gate.feature, now);
  }
  return (
    isActive(user.featuresUntil, 'explorer', now) ||
    (!!gate.habitatId && isActive(user.habitatsAccessUntil, gate.habitatId, now))
  );
};

/**
 * The budget for re-checking the entitlement after a payment. Exhausting it is not a failure —
 * the card was charged, so the modal says "still finalising" and offers a retry.
 */
export const ENTITLEMENT_POLL_BUDGET_MS = 45_000;

/**
 * How long to wait before each re-check, in milliseconds.
 *
 * The webhook usually beats the visitor back, so the first check normally succeeds and the rest
 * never run. Backing off keeps the rare slow case bounded without spending requests on the common
 * fast one. The last wait is clamped to whatever budget remains rather than being dropped whole,
 * so the schedule actually uses the budget it was given.
 */
export const entitlementPollDelays = (budgetMs: number = ENTITLEMENT_POLL_BUDGET_MS): number[] => {
  const delays: number[] = [];
  let delay = 1_000;
  let spent = 0;
  while (spent < budgetMs) {
    const next = Math.min(delay, budgetMs - spent);
    delays.push(next);
    spent += next;
    delay *= 2;
  }
  return delays;
};
